import { combineReducers, configureStore } from "@reduxjs/toolkit"
import { createTransform, persistReducer, persistStore, type PersistConfig, type Persistor } from "redux-persist"
import storage from "redux-persist/lib/storage" // Defaults to localStorage for web
import userReducer from "@/store/currentUserSlice" // Import the user slice
import sessionReducer from "@/store/sessionSlice" // Import the session slice
import sortReducer from "@/store/sortSlice" // Import the sort slice
import callingLeadReducer from "@/store/calling-lead"
import callRingDuration from "@/store/call-ring-duration"
import callDocumenter from "@/store/call-documenter"

import callList from "@/store/call-list"
import liveCallDialer from "@/store/liveCallDialer"
import chatReducer from "@/store/features/chatSlice" // Import the chat slice
import todoFormReducer from "@/store/features/todoFormSlice" // Import the todoForm slice
import emailFormReducer from "@/store/features/emailFormSlice" // Import the emailForm slice
import mailComposerReducer from "@/store/features/mailComposerSlice"
import noteFormReducer from "@/store/features/noteFormSlice" // Import the noteForm slice
import contactFormReducer from "@/store/features/contactFormSlice" // Import the contactForm slice
import calendarEventsReducer from "@/store/features/calendarEventsSlice" // Import the calendarEvents slice
import stOfferFormReducer from "@/store/features/stOfferFormSlice" // Import the ST offer form slice
import optimisticHistoryReducer from "@/store/features/optimisticHistorySlice" // Import the optimistic history slice
import topBarAlertsReducer from "@/store/features/topBarAlertsSlice" // Import the top-bar alerts slice
import { CallSlice } from "@/store/call"
import { dashboardSlice, DASHBOARD_PERSIST_WHITELIST, type DashboardState } from "@/store/dashboardSlice"
import type { TypedUseSelectorHook } from "react-redux"
import { useDispatch, useSelector, useStore } from "react-redux"

// Strip the Strapi JWT before the user slice is written to localStorage — the
// token must live in memory only (it is refetched via /api/auth/current-user
// on mount). XSS on any page must never be able to lift a 30-day bearer token
// out of storage.
const stripAuthToken = createTransform(
  (inboundState: {
    currentUser: ({ token?: string } & Record<string, unknown>) | null
  }) => {
    if (!inboundState?.currentUser?.token) return inboundState
    const currentUser = { ...inboundState.currentUser }
    delete currentUser.token
    return { ...inboundState, currentUser }
  },
  null,
  { whitelist: ["user"] }
)

const persistConfig = {
  key: "persist", // Key for the persisted data
  transforms: [stripAuthToken],
  blacklist: [
    // Don't persist real-time/sensitive slices
    "callingLead",
    "call",
    "callRingDuration",
    "callDocumenter",

    "chat",
    "todoForm",
    "emailForm",
    "mailComposer",
    "noteForm",
    "contactForm",
    "calendarEvents",
    "session", // Don't persist session - rely on actual server session
    "stOfferForm", // Don't persist ST offer form
    "optimisticHistory", // Don't persist optimistic history
    "dashboard", // Nested persist below keeps only the global filters; dashboards themselves are always re-fetched
    "topBarAlerts", // Always fetched fresh via useTopBarAlertsSync
    "liveCallDialer", // Real-time stream, always re-fetched on socket reconnect
  ], // Key of the slices to exclude from persistence
  storage, // Use localStorage
}

// Dashboard filters (date range + user filter) survive reloads; everything else
// in the slice (edit mode, pending layout, loaded dashboards) stays in memory.
// Stored under its own "persist:dashboard" key; purgeClientCaches() wipes it on
// logout / agency switch together with the root blob. On the server this
// wrapper is a plain passthrough until PERSIST is dispatched, which only the
// client does.
const dashboardPersistConfig: PersistConfig<DashboardState> = {
  key: "dashboard",
  storage,
  whitelist: DASHBOARD_PERSIST_WHITELIST,
}

// Combine reducers
const rootReducer = combineReducers({
  user: userReducer, // User slice (persisted WITHOUT the auth token — see stripAuthToken)
  session: sessionReducer, // Session slice (persisted - just tracks if session exists)
  sort: sortReducer, // Sort slice (persisted - user preferences)
callingLead: callingLeadReducer,
  callRingDuration: callRingDuration,
  callDocumenter: callDocumenter,

  call: CallSlice.reducer,
  callList: callList,
  liveCallDialer: liveCallDialer,
  chat: chatReducer, // Chat slice
  todoForm: todoFormReducer, // TodoForm slice
  emailForm: emailFormReducer, // EmailForm slice
  mailComposer: mailComposerReducer, // E-mail composer on the lead page
  noteForm: noteFormReducer, // NoteForm slice
  contactForm: contactFormReducer, // ContactForm slice
  calendarEvents: calendarEventsReducer, // CalendarEvents slice
  stOfferForm: stOfferFormReducer, // ST offer form slice
  optimisticHistory: optimisticHistoryReducer, // Optimistic history slice
  dashboard: persistReducer(dashboardPersistConfig, dashboardSlice.reducer), // Dashboard builder slice (filters persisted, see above)
  topBarAlerts: topBarAlertsReducer, // Top-bar alerts slice (calls/emails/todos/chat per membership)
})

// Make configured store
const makeConfiguredStore = () => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false // Disable serializable check for redux-persist
      })
  })
}

interface PersistedStore extends ReturnType<typeof makeConfiguredStore> {
  // Only set on the client (makeStore skips persistStore during SSR).
  __persistor?: Persistor
}

// Export a function to create a store
export const makeStore = (): PersistedStore => {
  const isServer =
    process.env.NODE_ENV === "production"
      ? typeof window === "undefined"
      : typeof window !== "object"

  if (isServer) {
    // If on server, return a configured store without persisting
    return makeConfiguredStore() as PersistedStore
  } else {
    // One-time cleanup: older builds persisted the user slice INCLUDING the
    // Strapi JWT. Strip any already-stored token before rehydration so the
    // stale credential doesn't linger in localStorage.
    try {
      const raw = window.localStorage.getItem("persist:persist")
      if (raw && raw.includes('"token"')) {
        const root = JSON.parse(raw)
        if (typeof root?.user === "string") {
          const userSlice = JSON.parse(root.user)
          if (userSlice?.currentUser?.token) {
            delete userSlice.currentUser.token
            root.user = JSON.stringify(userSlice)
            window.localStorage.setItem("persist:persist", JSON.stringify(root))
          }
        }
      }
    } catch {
      // corrupt persist blob — redux-persist rewrites it on the next flush
    }

    // If on client, create a persisted reducer
    // Type-only cast: the transform's slice-level generics otherwise narrow
    // persistReducer's root-state inference to Partial<…> and break tsc.
    const persistedReducer = persistReducer(persistConfig as PersistConfig<ReturnType<typeof rootReducer>>, rootReducer)
    const store = configureStore({
      reducer: persistedReducer,
      middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
          serializableCheck: false // Disable serializable check for redux-persist
        })
    })

    // Create a persistor
    ;(store as PersistedStore).__persistor = persistStore(store) // Add persistor to the store
    return store as PersistedStore
  }
}

// Define inferred types for store, state, and dispatch
export type AppStore = ReturnType<typeof makeStore> // Use the makeStore function to infer the type
export type RootState = ReturnType<AppStore["getState"]>
export type AppDispatch = AppStore["dispatch"]

// Create an instance of the store
export const store = makeStore()

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
export const useAppStore = useStore.withTypes<AppStore>()
